import { Col, Row, Table, Button, Typography } from "antd";
import React, { useMemo, useState } from "react";
import {
  maScaleOptions,
  mmtScaleOptions,
  vcgScaleOptions,
} from "../../../IpEMR/Options/physioOptions";
import NoDataPage from "../../../NoDataPages/NoDataPage";
import scaleIcon from "../../../../assets/images/rating-scale 1.svg";

const scaleOptionsMap = {
  MAS: maScaleOptions,
  MMT: mmtScaleOptions,
  VCG: vcgScaleOptions,
};

const getScaleOptions = (scaleKey) => {
  return scaleOptionsMap[scaleKey] || [];
};

const getScaleLabel = (score, scaleKey) => {
  const scaleOptions = getScaleOptions(scaleKey);
  const option = scaleOptions.find((item) => item.value === score);
  return option ? option.label : "N/A";
};

const ExpandableText = ({ text, collapsedLines = 2 }) => {
  const [expanded, setExpanded] = useState(false);
  const value = (text ?? "").toString();
  const trimmed = value.trim();
  const display = trimmed.length ? trimmed : "N/A";

  // Simple heuristic: show toggle only for reasonably long text
  const shouldToggle = trimmed.length > 60 || trimmed.includes("\n");

  return (
    <div
      style={{
        maxWidth: 220,
        overflowWrap: "anywhere",
        wordBreak: "break-word",
      }}
    >
      <Typography.Paragraph
        style={{
          marginBottom: 0,
          whiteSpace: "pre-wrap",
          overflowWrap: "anywhere",
          // For very long unbroken strings (common in mobile): force wrap.
          wordBreak: "break-all",
        }}
        ellipsis={
          !shouldToggle
            ? false
            : expanded
            ? false
            : { rows: collapsedLines }
        }
      >
        {display}
      </Typography.Paragraph>
      {shouldToggle && trimmed.length > 0 && (
        <Button
          type="link"
          size="small"
          style={{ padding: 0, height: "auto" }}
          onClick={() => setExpanded((v) => !v)}
        >
          {expanded ? "Less" : "More"}
        </Button>
      )}
    </div>
  );
};

const getColumns = (onEdit) => [
  {
    title: "Date",
    dataIndex: "date",
    key: "date",
    render: (value) => (
    <span style={{ whiteSpace: "nowrap" }}>{value}</span>
  ),
  },
  {
    title: <strong>LEFT</strong>,
    children: [
          {
            title: "Upper Limb Interpretation",
            dataIndex: "upper_left_limb_score",
            key: "upper_left_limb_interpretation",
            width: 220,
            render: (score, record) => getScaleLabel(score, record.scaleKey),
          },
          {
            title: "Lower Limb Interpretation",
            dataIndex: "lower_left_limb_score",
            key: "lower_left_limb_interpretation",
            width: 220,
            render: (score, record) => getScaleLabel(score, record.scaleKey),
          },
        
    ],
  },
  {
    title: <strong>RIGHT</strong>,
    children: [
    
          {
            title: "Upper Limb Interpretation",
            dataIndex: "upper_right_limb_score",
            key: "upper_right_limb_interpretation",
            width: 220,
            render: (score, record) => getScaleLabel(score, record.scaleKey),
          },
          {
            title: "Lower Limb Interpretation",
            dataIndex: "lower_right_limb_score",
            key: "lower_right_limb_interpretation",
            width: 220,
            render: (score, record) => getScaleLabel(score, record.scaleKey),
          },
    ],
  },
  {
    title: "Comments",
    dataIndex: "comments",
    key: "comments",
    width: 220,
    render: (text) => <ExpandableText text={text} collapsedLines={2} />,
  },
  {
  title: "Action",
  render: (_, record) =>
    record.type === "EMR" ? null : (
      <Button type="link" onClick={() => onEdit(record)}>
        Edit
      </Button>
    ),
},


];

const coordinationTestColumns = (onEdit) => [
  {
    title: "Date",
    dataIndex: "date",
    key: "date",
    render: (value) => (
    <span style={{ whiteSpace: "nowrap" }}>{value}</span>
  ),
  },
  {
    title: "Finger to Nose",
    dataIndex: "finger_to_nose",
    key: "finger_to_nose",
  },
  {
    title: "Finger to Finger",
    dataIndex: "finger_to_finger",
    key: "finger_to_finger",
  },
  {
  title: (
    <div style={{ textAlign: "center", lineHeight: "16px" }}>
      <div>Adiadochokinesia or</div>
      <div>Dysdiadokokinesia</div>
    </div>
  ),
  dataIndex: "adiadochokinesia_or_dysdiadokokinesia",
  key: "adiadochokinesia_or_dysdiadokokinesia",
  width: 140,
  render: (value) => (
    <span
      style={{
        display: "inline-block",
        maxWidth: 120,
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
      }}
      title={value}
    >
      {value || "N/A"}
    </span>
  ),
},
{
    title: "Rebound Phenomenon",
    dataIndex: "rebound_phenomenon",
    key: "rebound_phenomenon",
  },
  {
    title: "Walking Along Straight Line",
    dataIndex: "walking_along_straight_line",
    key: "walking_along_straight_line",
  },
  {
    title: "Romberg's Test",
    dataIndex: "rombergs_test",
    key: "rombergs_test",
  },
  
  {
    title: "Heel to Shin",
    dataIndex: "heel_to_shin",
    key: "heel_to_shin",
  },
  
  {
    title: "Comments",
    dataIndex: "comments",
    key: "comments",
    width: 220,
    render: (text) => <ExpandableText text={text} collapsedLines={2} />,
  },
  {
  title: "Action",
  render: (_, record) =>
    record?.rawData?.type === "EMR" ? null : (
      <Button
        type="link"
        onClick={() =>
          onEdit({
            id: record.id,
            data: record.rawData,
          })
        }
      >
        Edit
      </Button>
    ),
},


];

const ScaleTable = ({ scaleKey, data, onEdit }) => {
  const dataSource = useMemo(() => {
    return data.map((item, index) => {
      if (scaleKey === "FingerGripTest") {
      return {
        key: index,
        date: new Date(item?.created_at).toLocaleString(),
        age: item?.data?.age,
        left_hand: item?.data?.left_hand,
        right_hand: item?.data?.right_hand,
        comments: item?.data?.comments,
        type: item?.data?.type || item?.type
      };
    }
      if (scaleKey === "CoordinationTest") {
        return {
          key: index,
          id: item?.data?.id,
          rawData: item.data,
          date: new Date(item?.created_at).toLocaleDateString("en-US", {
            day: "2-digit",
            month: "short",
            // year: "2-digit",
            hour: "2-digit",
            minute: "2-digit",
            // second: "2-digit",
            hour12: true,
          }),
          adiadochokinesia_or_dysdiadokokinesia:
          item?.data?.adiadochokinesia_or_dysdiadokokinesia ??
          item?.data?.adiadokokinesia ??
          "N/A",
          finger_to_finger: item?.data?.finger_to_finger ??
          item?.data?.finger_to_finger_test ??
          "N/A",
          finger_to_nose: item?.data?.finger_to_nose??
          item?.data?.finger_to_nose_test??
          "N/A",
          heel_to_shin: item?.data?.heel_to_shin ??
          item?.data?.heel_on_shin_test ??
          "N/A",
          rebound_phenomenon: item?.data?.rebound_phenomenon ??
          item?.data?.rebound_phenomena ??
          "N/A",
          rombergs_test: item?.data?.rombergs_test || "N/A",
          walking_along_straight_line: item?.data?.walking_along_straight_line??
          item?.data?.walking_along_a_straight_line??
          "N/A",
          comments: item?.data?.comments ?? "N/A",
        };
      }

      return {
        
        id: item?.data?.id,
        key: index,
        date: new Date(item?.created_at).toLocaleDateString("en-US", {
          day: "2-digit",
          month: "short",
          // year: "2-digit",
          hour: "2-digit",
          minute: "2-digit",
          // second: "2-digit",
          hour12: true,
        }),
        upper_left_limb_score: item?.data?.upper_left_limb,
        lower_left_limb_score: item?.data?.lower_left_limb,
        upper_right_limb_score: item?.data?.upper_right_limb,
        lower_right_limb_score: item?.data?.lower_right_limb,
        comments: item?.data?.comments,
        scaleKey: scaleKey,
        type: item?.data?.type, 
      };
    });
  }, [data, scaleKey]);

  return (
    <Row justify="center">
      <Col>
        <Table
          dataSource={dataSource}
          columns={
            scaleKey === "CoordinationTest" ? coordinationTestColumns(onEdit)  : getColumns(onEdit)
          }
          pagination={false}
          tableLayout="fixed"
          scroll={{ x: "max-content" }}
          locale={{
            emptyText: (
              <NoDataPage
                title="No data was recorded for this scale"
                subtitle=""
                image={scaleIcon}
              />
            ),
          }}
          style={{
            border: "1px solid white",
            borderRadius: "4px",
            boxShadow: "0px -2px 3px rgba(0, 0, 0, 0.05)",
          }}
        />
      </Col>
    </Row>
  );
};

export default ScaleTable;
